import React from 'react';
import { StaticPage } from '../StaticPage';
import { useLanguage } from '../../i18n/useLanguage';

export const SymbolPage: React.FC = () => {
  const { language } = useLanguage();
  return (
    <StaticPage titleKey="static.symbol">
      {language === 'de' ? <SymbolContentDe /> : <SymbolContentEn />}
    </StaticPage>
  );
};

const SymbolContentDe: React.FC = () => (
  <>
    <h2>:|: – das kleine Zeichen für den großen Zufall</h2>
    <p>
      Manchmal ist da dieser eine Blick. Dieses kurze Lächeln. Dieser Moment,
      in dem man spürt: <em>Da war etwas.</em>
    </p>
    <p>Und dann? Nichts. Zu früh vorbei. Zu spät reagiert. Verpasst.</p>
    <p>
      <strong>Genau dafür gibt es unser Zeichen :|:.</strong>
    </p>
    <p>
      Es ist ein leises, charmantes Signal für alle, die sich nicht trauen, sofort etwas zu sagen – aber trotzdem
      einen kleinen Funken aussenden möchten.
    </p>
    <p>
      Ein stilles <em>„Ich habe dich bemerkt.“</em> Ein freundliches{' '}
      <em>„Vielleicht denke ich gerade an dich.“</em> Ein verspieltes{' '}
      <strong>
        <em>„I think it&apos;s you“</em>
      </strong>
      .
    </p>
    <p>
      Unser <strong>Symbol :|:</strong> kann auf ganz unterschiedliche Weise verwendet werden: mit dem Finger auf
      eine beschlagene Scheibe gezeichnet, auf eine Serviette gekritzelt, mit dem Löffel auf den Tellerrand getippt,
      ganz leise an den Tisch geklopft, mit einem kleinen Klingeln angedeutet – oder sogar einfach geblinzelt.
    </p>
    <p>Immer im selben Rhythmus:</p>
    <p className="static-page-quote">
      <strong>ding ding dong ding ding</strong>
      <br />
      <strong>kurz – kurz – lang – kurz – kurz</strong>
    </p>
    <p>
      Wer das Zeichen kennt, versteht: Hier möchte jemand nicht stören, nicht bedrängen, nicht plump anmachen –
      sondern auf feine, originelle Weise Kontakt aufnehmen.
    </p>
    <p>
      Das Schöne daran: Es bleibt leicht. Kein Druck. Kein peinlicher Spruch. Kein überhasteter Moment. Nur ein kleines
      gemeinsames Geheimnis inmitten des Alltags.
    </p>
    <p>
      Und wenn das Zeichen erwidert wird oder der Moment später nachklingt, kann man sich auf I Think It&apos;s You
      wiederfinden, die Nachricht „morsen“ und sich online verabreden. So wird aus einer flüchtigen Begegnung
      vielleicht doch noch ein echtes Wiedersehen.
    </p>
    <p>
      <strong>:|: ist mehr als ein Symbol.</strong>
      <br />
      Es ist eine zweite Chance für alle verpassten ersten Male.
    </p>
  </>
);

const SymbolContentEn: React.FC = () => (
  <>
    <h2>:|: — the little sign for life&apos;s big coincidences</h2>
    <p>
      Sometimes there&apos;s that one look. That brief smile. That moment when you feel:{' '}
      <em>There was something there.</em>
    </p>
    <p>And then? Nothing. Over too soon. Reacted too late. Missed it.</p>
    <p>
      <strong>That&apos;s exactly what our sign :|: is for.</strong>
    </p>
    <p>
      It&apos;s a quiet, charming signal for anyone who doesn&apos;t dare speak up right away — but still wants to
      send out a little spark.
    </p>
    <p>
      A soft <em>&ldquo;I noticed you.&rdquo;</em> A friendly <em>&ldquo;Maybe I&apos;m thinking of you right now.&rdquo;</em>{' '}
      A playful{' '}
      <strong>
        <em>&ldquo;I think it&apos;s you.&rdquo;</em>
      </strong>
    </p>
    <p>
      Our <strong>symbol :|:</strong> can be used in all kinds of ways: traced with a finger on a fogged-up window,
      scribbled on a napkin, tapped on the rim of a plate with a spoon, knocked softly on the table, hinted at with a
      tiny chime — or even with a simple blink.
    </p>
    <p>Always the same rhythm:</p>
    <p className="static-page-quote">
      <strong>ding ding dong ding ding</strong>
      <br />
      <strong>short — short — long — short — short</strong>
    </p>
    <p>
      Anyone who knows the sign understands: someone here doesn&apos;t want to intrude, crowd, or come on too strong —
      they want to reach out in a subtle, original way.
    </p>
    <p>
      The beauty of it: it stays light. No pressure. No awkward line. No rushed moment. Just a small shared secret in
      the middle of everyday life.
    </p>
    <p>
      And if the sign is returned or the moment lingers later, you can find each other again on{' '}
      <strong>I Think It&apos;s You</strong>, &ldquo;Morse&rdquo; the message, and arrange to meet online. That way a
      fleeting encounter might still turn into a real reunion.
    </p>
    <p>
      <strong>:|: is more than a symbol.</strong>
      <br />
      It&apos;s a second chance for every missed first time.
    </p>
  </>
);
